import { IDunnings } from '@monetize/types/app';
import { formatInteger } from '@monetize/utils/core';
import { createColumnHelper } from '../../../../components/Monetize/DataTableNew/appTable';
import { Link as ReactDomLink } from 'react-router-dom';
import {
  MFlex,
  MLink,
  MSkeleton,
  MTag,
  MText,
} from '../../../../components/Monetize';
import { actionCatalogColumn } from '../../../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  getColHeaderWithSize,
  textBodyTemplate,
} from '../../../../components/Monetize/DataTableNew/tableUtilsNew';
import { ROUTES } from '../../../../constants';
import { pluralize } from '../../../../utils';

const columnHelper = createColumnHelper<IDunnings>();

export interface DunningListColumnContext extends ColumnContextBase<IDunnings> {
  billGroupsCounts: Record<string, number>;
}

/** tableId: local filter state (no FilterStateKeys) */
export const DUNNING_LIST_CATALOG: ColumnCatalog<
  IDunnings,
  DunningListColumnContext
> = [
  {
    id: 'name',
    label: 'Name',
    customizable: false,
    build: () =>
      columnHelper.accessor('name', {
        ...getColHeaderWithSize('Name', 250),
        cell: ({ row }) => {
          const dunning = row.original as IDunnings;
          return (
            <MFlex alignItems="center" gap="2">
              <MText>{dunning.name}</MText>
              {dunning.defaultProcess && (
                <MTag size="sm" variant="green">
                  Default
                </MTag>
              )}
            </MFlex>
          );
        },
        enableSorting: false,
      }),
  },
  {
    id: 'dunningProcessDays',
    label: 'Dunning Period',
    build: () =>
      columnHelper.accessor('dunningProcessDays', {
        ...getColHeaderWithSize('Dunning Period', 180),
        cell: ({ row }) => (
          <MText>{row.original.dunningProcessDays} Days</MText>
        ),
        enableSorting: false,
      }),
  },
  {
    id: 'totalDunningStep',
    label: 'Number of Steps',
    build: () =>
      columnHelper.accessor('totalDunningStep', {
        ...getColHeaderWithSize('Number of Steps', 200),
        cell: textBodyTemplate(),
        enableSorting: false,
      }),
  },
  {
    id: 'billGroups',
    label: 'Assigned Bill Groups',
    build: ({ billGroupsCounts }) =>
      columnHelper.display({
        id: 'billGroups',
        ...getColHeaderWithSize('Assigned Bill Groups', 220),
        cell: ({ row }) => {
          const dunning = row.original as IDunnings;
          const count = billGroupsCounts[dunning.id];

          if (count === undefined) {
            return <MSkeleton w="100px" height={5} />;
          }

          if (count === 0) {
            return <MText>None</MText>;
          }

          const formattedCount = formatInteger(count);
          const text = `${formattedCount} ${pluralize('Bill Group', count)}`;

          return (
            // Render as a real <a href> via react-router Link so the link is
            // keyboard-focusable, picks up the V2 Link focus halo, and
            // supports cmd/ctrl-click to open in a new tab. MLink with only
            // an onClick (no href/to) renders an unfocusable anchor.
            <MLink asChild>
              <ReactDomLink
                to={`${ROUTES.BILLING_DUNNING_MANAGEMENT}?dunningProcessId=${dunning.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                {text}
              </ReactDomLink>
            </MLink>
          );
        },
        enableSorting: false,
      }),
  },
  actionCatalogColumn<IDunnings>(),
];
